import { useMemo } from "react";
import clsx from "clsx";
import type { CalendarEvent } from "../../api";

interface Props {
  events: CalendarEvent[];
  busyId: string | null;
  onEdit: (event: CalendarEvent) => void;
  onDelete: (id: string) => void;
}

/**
 * Chronological list of events grouped by date. Date headers render in
 * Pacific time. Extracted from CalendarModal in C.1.6 so the LIST mode
 * is symmetric with WEEK and MONTH.
 */
export function CalendarListView({ events, busyId, onEdit, onDelete }: Props) {
  const grouped = useMemo(() => {
    const byDate = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const date = e.startAt.slice(0, 10);
      const list = byDate.get(date) ?? [];
      list.push(e);
      byDate.set(date, list);
    }
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, list]) => ({ date, events: list }));
  }, [events]);

  if (events.length === 0) {
    return (
      <div className="calendar-empty">
        No upcoming events. Click + NEW EVENT to add one.
      </div>
    );
  }

  return (
    <>
      {grouped.map(({ date, events }) => (
        <div className="calendar-day-group" key={date}>
          <div className="calendar-day-header">{formatDate(date)}</div>
          {events.map((evt) => (
            <EventRow
              key={evt.id}
              event={evt}
              busy={busyId === evt.id}
              onEdit={() => onEdit(evt)}
              onDelete={() => onDelete(evt.id)}
            />
          ))}
        </div>
      ))}
    </>
  );
}

function EventRow({
  event,
  busy,
  onEdit,
  onDelete,
}: {
  event: CalendarEvent;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className={clsx("calendar-event-row", busy && "busy")}>
      <div className="calendar-event-time">
        {event.allDay ? "ALL DAY" : formatTime(event.startAt)}
      </div>
      <div
        className={clsx(
          "calendar-event-priority",
          `priority-${event.priority}`,
        )}
        title={`Priority: ${event.priority.toUpperCase()}`}
      />
      <div className="calendar-event-content">
        <div className="calendar-event-title">{event.title}</div>
        {(event.location || event.dealName) && (
          <div className="calendar-event-meta">
            {event.dealName && (
              <span className="calendar-event-deal">{event.dealName.toUpperCase()}</span>
            )}
            {event.location && (
              <span className="calendar-event-loc">📍 {event.location}</span>
            )}
          </div>
        )}
      </div>
      <div className="calendar-event-actions">
        <button onClick={onEdit} disabled={busy} title="Edit">
          EDIT
        </button>
        <button onClick={onDelete} disabled={busy} title="Delete">
          ✕
        </button>
      </div>
    </div>
  );
}

function formatDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "America/Los_Angeles",
  });
}

function formatTime(isoUTC: string): string {
  const d = new Date(isoUTC);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/Los_Angeles",
  });
}
